import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Check, Star } from 'lucide-react';
import { Button } from './ui/Button';
const PLANS = [{
  id: 'standard',
  name: 'OPCIÓN ESTÁNDAR',
  tagline: 'El manual completo',
  priceOld: '$34.990',
  price: '$19.990',
  checkoutUrl: 'https://checkout.palabravivasoporte.com/cart/47731589382307:1',
  features: ['5 Módulos Clínicos completos en PDF', '3 Súper Bonos VIP (Script Book, Kit Clínico, Mentoría)', 'Acceso inmediato y de por vida']
}, {
  id: 'elite',
  name: 'OPCIÓN ÉLITE',
  tagline: 'Pack completo + Supervisión',
  priceOld: '$43.990',
  price: '$27.990',
  checkoutUrl: 'https://checkout.palabravivasoporte.com/cart/47736367480995:1',
  badge: 'LA MÁS ELEGIDA',
  features: ['Todo lo de la Opción Estándar (5 Módulos + 3 Bonos)', "Módulo ÉLITE 'Supervisión de Casos Reales' (fibromialgia, ansiedad, bloqueos)", 'Protocolo de Ética Clínica + Anti-Burnout']
}] as const;
type PlanId = (typeof PLANS)[number]['id'];
interface PricingSelectorProps {
  variant?: 'light' | 'dark';
  ctaLabel?: string;
}
export const PricingSelector: React.FC<PricingSelectorProps> = ({
  variant = 'light',
  ctaLabel
}) => {
  const [selected, setSelected] = useState<PlanId>('elite');
  const selectedPlan = PLANS.find(p => p.id === selected)!;
  const handleBuy = () => {
    window.open(selectedPlan.checkoutUrl, '_blank', 'noopener,noreferrer');
  };
  const isDark = variant === 'dark';
  return <div className="w-full max-w-4xl mx-auto" data-id="element-226">
      <div className="flex flex-col gap-3 md:gap-4 mb-6" data-id="element-227">
        {PLANS.map(plan => {
        const isSelected = selected === plan.id;
        const isElite = plan.id === 'elite';
        return <motion.button key={plan.id} type="button" onClick={() => setSelected(plan.id)} whileHover={{
          x: 2
        }} transition={{
          duration: 0.2
        }} className={`relative w-full text-left rounded-sm border-2 transition-all overflow-hidden ${isSelected ? isDark ? 'border-gold-500 bg-navy-800/90 shadow-[0_0_25px_rgba(201,163,92,0.25)]' : 'border-gold-500 bg-white shadow-[0_0_25px_rgba(201,163,92,0.2)]' : isDark ? 'border-gold-500/15 bg-navy-800/40 hover:border-gold-500/40' : 'border-gold-500/20 bg-white/60 hover:border-gold-500/50'}`} data-id="element-228">
              {/* Elite badge — top right corner */}
              {isElite && plan.badge && <div className="absolute top-0 right-0 bg-gold-500 text-navy-900 text-[10px] font-bold tracking-widest px-2.5 py-1 rounded-bl-sm flex items-center gap-1 shadow-md z-10" data-id="element-229">
                  <Star className="w-3 h-3 fill-current" data-id="element-230" />
                  {plan.badge}
                </div>}

              <div className="flex items-stretch gap-4 p-4 md:p-5" data-id="element-231">
                {/* Radio */}
                <div className="flex items-center" data-id="element-232">
                  <div className={`flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${isSelected ? 'border-gold-500 bg-gold-500' : isDark ? 'border-cream/30' : 'border-navy/30'}`} data-id="element-233">
                    {isSelected && <motion.div initial={{
                  scale: 0
                }} animate={{
                  scale: 1
                }} className="w-2 h-2 rounded-full bg-navy-900" data-id="element-234" />}
                  </div>
                </div>

                {/* Title + features */}
                <div className="flex-1 min-w-0 pr-2" data-id="element-235">
                  <div className="flex items-baseline gap-2 mb-2 flex-wrap" data-id="element-236">
                    <h3 className={`font-serif font-bold text-base md:text-lg leading-tight ${isDark ? 'text-cream' : 'text-navy'}`} data-id="element-237">
                      {plan.name}
                    </h3>
                    <p className={`text-xs ${isDark ? 'text-cream-200/60' : 'text-navy/60'}`} data-id="element-238">
                      · {plan.tagline}
                    </p>
                  </div>

                  <ul className="space-y-1" data-id="element-239">
                    {plan.features.map((f, i) => <li key={i} className="flex items-start gap-1.5" data-id="element-240">
                        <Check className={`w-3 h-3 flex-shrink-0 mt-1 ${isElite ? 'text-gold-500' : 'text-gold-600'}`} data-id="element-241" />
                        <span className={`text-xs leading-snug ${isDark ? 'text-cream-200/85' : 'text-navy/75'}`} data-id="element-242">
                          {f}
                        </span>
                      </li>)}
                  </ul>
                </div>

                {/* Price column — right side */}
                <div className={`flex flex-col items-end justify-center pl-3 md:pl-4 border-l ${isDark ? 'border-gold-500/20' : 'border-gold-500/20'} min-w-[100px] md:min-w-[140px]`} data-id="element-243">
                  <span className={`line-through text-xs md:text-sm ${isDark ? 'text-cream/40' : 'text-navy/40'}`} data-id="element-244">
                    {plan.priceOld}
                  </span>
                  <span className={`text-2xl md:text-3xl font-bold font-serif leading-tight ${isDark ? isElite ? 'text-gold-400' : 'text-cream' : 'text-navy'}`} data-id="element-245">
                    {plan.price}
                  </span>
                  {isElite && <span className="text-[9px] uppercase tracking-widest text-gold-500 font-bold mt-0.5" data-id="element-246">
                      Recomendado
                    </span>}
                </div>
              </div>
            </motion.button>;
      })}
      </div>

      {/* Shared CTA */}
      <motion.div key={selected} initial={{
      opacity: 0.7,
      scale: 0.99
    }} animate={{
      opacity: 1,
      scale: 1
    }} transition={{
      duration: 0.25
    }} className="text-center" data-id="element-247">
        <Button size="lg" onClick={handleBuy} className="w-full sm:w-auto text-base md:text-lg px-8" data-id="element-248">
          {ctaLabel || `💥 Comprar ${selectedPlan.name} — ${selectedPlan.price}`}
        </Button>
        <p className={`text-xs mt-3 ${isDark ? 'text-cream-200/50' : 'text-navy/50'}`} data-id="element-249">
          Pago seguro · Acceso inmediato · Garantía 7 días
        </p>
      </motion.div>
    </div>;
};